/**
 * Archiving a case, and acting on a selection of cases. Section 4 of the
 * request of 24 September 2026: "allow option to select multiple cases in
 * orders, archive, production line, and allow different actions for the
 * selected cases like: delete all, archive all, assign all to a particular
 * department or person".
 *
 * The archive held one kind of case until now, the dispatched. An archived case
 * is the other: taken off every list of work in hand (the orders, the
 * production line, the queues, the board) without leaving the lab and without
 * being deleted, because it is finished business the system never saw go out,
 * or work the lab has stopped. It keeps its record and its history, sits in the
 * archive's Archived tab, and comes back exactly where it was, assignee and all.
 *
 * Not an order state. Like a deletion it is orthogonal to readiness, hold,
 * payment and dispatch, so it is a block of its own beside them.
 *
 * The refusals are the API's words, and the same strings live in its
 * ArchiveRules and BulkRules.
 */

export const ARCHIVE_REASON_MAX = 200;

/** As many cases as the largest page holds. */
export const BULK_MAX = 200;

export const ARCHIVE_REFUSAL = {
  reasonTooLong: `Keep the reason to ${ARCHIVE_REASON_MAX} characters`,
  alreadyArchived: 'This case is already archived.',
  notArchived: 'This case is not archived.',
  dispatched: 'This case has left the lab, and is in the archive already.',
  /** What every command that works a tray says of an archived one. */
  isArchived: 'This case is archived. Take it out of the archive to work on it.',
} as const;

export const BULK_REFUSAL = {
  chooseOne: 'Choose at least one case.',
  tooMany: `Choose at most ${BULK_MAX} cases at a time.`,
  notFound: 'This case is not in the system.',
  chooseSomething: 'Choose a department, a person, or both.',
  /** Ready is what passing Final Check means, so no list sends a case there. */
  notToReady: 'A case reaches Ready by passing Final Check.',
  /** Nor is a case that has left the lab sent anywhere or given to anybody. */
  leftTheLab: 'This case has left the lab.',
} as const;

/** The archive block on a job order. Every field but `archived` is null while it is not. */
export interface JobOrderArchive {
  archived: boolean;
  archivedAt: string | null;
  archivedByUserId: string | null;
  archivedByUserName: string | null;
  /** Why, when somebody said. Most archived cases are finished work, and say nothing. */
  reason: string | null;
}

/**
 * Whether a case is archived, read through here rather than off the block.
 *
 * The block may be absent although the type says otherwise: the production
 * client passes responses through unvalidated, so an API that has not shipped
 * the archive yet sends orders without one, and reading `archive.archived` off
 * them throws. That is how a missing field took the job screen down on
 * 23 September 2026. Absent reads as not archived.
 */
export function isArchivedCase(subject: { archive?: JobOrderArchive | null }): boolean {
  return subject.archive?.archived === true;
}

/** One case of a selection, as the API answers it: done, or refused in the words to show. */
export interface BulkOutcome {
  id: string;
  ok: boolean;
  detail: string | null;
}

/**
 * The refusals in an answer, one line each, grouped by what was said: twelve
 * cases already archived read as one reason with twelve numbers under it, not
 * twelve sentences.
 */
export function refusalsByReason(outcomes: readonly BulkOutcome[]): { detail: string; ids: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const outcome of outcomes) {
    if (outcome.ok) continue;
    const detail = outcome.detail ?? BULK_REFUSAL.notFound;
    groups.set(detail, [...(groups.get(detail) ?? []), outcome.id]);
  }
  return [...groups].map(([detail, ids]) => ({ detail, ids }));
}
